"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { MapPin, Phone, Mail, Clock, Calendar as CalendarIcon, CircleCheck, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Link } from "@/components/common/Link";
const StyledSelect = React.forwardRef<HTMLSelectElement, React.ComponentProps<"select">>(({
  className,
  ...props
}, ref) => <div className="relative">
      <select ref={ref} className={cn("flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 appearance-none", className)} {...props} />
      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center px-2 text-muted-foreground">
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4"><path d="M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657 11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803 10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path></svg>
      </div>
    </div>);
StyledSelect.displayName = "StyledSelect";
const StyledTextarea = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<"textarea">>(({
  className,
  ...props
}, ref) => <textarea ref={ref} className={cn("flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50", className)} {...props} />);
StyledTextarea.displayName = "StyledTextarea";
function AppointmentFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="42744" className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="w-20 h-20 bg-secondary/10 rounded-lg flex items-center justify-center">
          <CircleCheck className="w-10 h-10 text-secondary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-bold text-foreground font-default">פנייתך התקבלה בהצלחה!</h3>
          <p className="text-muted-foreground max-w-md mx-auto font-default">{successMessage}</p>
        </div>
        <Button asChild onClick={resetForm} variant="outline" className="mt-4 font-default">
          <Link to="tel:+97239005000">
            שליחת פנייה נוספת
          </Link>
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6 py-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        
        <FormField control={control} name="full_name" rules={{
        required: "שדה חובה"
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
              <FormLabel className="font-default">שם מלא של המטופל או איש קשר</FormLabel>
              <FormControl>
                <Input placeholder="ישראל ישראלי" {...field} className="focus:ring-accent" />
              </FormControl>
              <FormDescription className="font-default">הכנס את השם המלא לזיהוי מהיר</FormDescription>
              <FormMessage />
            </FormItem>} />

        
        <FormField control={control} name="email" rules={{
        required: "שדה חובה",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "כתובת אימייל לא תקינה"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">כתובת אימייל</FormLabel>
              <FormControl>
                <Input type="email" placeholder="care@siudy.co.il" {...field} className="focus:ring-accent" />
              </FormControl>
              <FormDescription className="font-default">לשליחת פרטי המטפל והסכם שירות</FormDescription>
              <FormMessage />
            </FormItem>} />

        
        <FormField control={control} name="phone" rules={{
        required: "שדה חובה",
        pattern: {
          value: /^[\d\-\+]{9,15}$/,
          message: "מספר טלפון לא תקין"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">טלפון ליצירת קשר מיידי</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="050-0000000" {...field} className="focus:ring-accent" />
              </FormControl>
              <FormDescription className="font-default">נחזור אליך תוך 15 דקות בדיוק</FormDescription>
              <FormMessage />
            </FormItem>} />

        
        <FormField control={control} name="appointment_date" rules={{
        required: "שדה חובה"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">תאריך תחילת שירות</FormLabel>
              <FormControl>
                <Input type="date" {...field} className="focus:ring-accent" />
              </FormControl>
              <FormDescription className="font-default">מתי תרצו שהמטפל יגיע?</FormDescription>
              <FormMessage />
            </FormItem>} />

        
        <FormField control={control} name="appointment_time" rules={{
        required: "שדה חובה"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">רמת דחיפות</FormLabel>
              <FormControl>
                <StyledSelect {...field} className="focus:ring-accent">
                  <option value="" disabled>בחר רמת דחיפות</option>
                  {["מיידי (תוך 5 שעות)", "היום", "מחר בבוקר", "תחילת שבוע הבא"].map(time => <option key={time} value={time}>{time}</option>)}
                </StyledSelect>
              </FormControl>
              <FormDescription className="font-default">אנו ערוכים למענה מהיר בכל הארץ</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      
      <FormField control={control} name="visit_reason" rules={{
      required: "שדה חובה"
    }} render={({
      field
    }) => <FormItem className="space-y-3">
            <FormLabel className="font-default">סוג השירות המבוקש</FormLabel>
            <FormControl>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {["מטפל סיעודי בבית", "השגחה בבית חולים", "מטפל סיעודי פרטי", "מטפל/ת לילה", "ייעוץ והכוונה"].map(option => <label key={option} className={cn("flex items-center space-x-3 space-x-reverse rounded-md border border-input p-3 cursor-pointer transition-colors hover:bg-accent/10 hover:text-accent-foreground", field.value === option ? "bg-accent/20 border-accent ring-1 ring-accent" : "bg-transparent")}>
                    <input type="radio" value={option} checked={field.value === option} onChange={field.onChange} className="h-4 w-4 border-accent text-accent focus:ring-accent" />
                    <span className="text-sm font-medium leading-none font-default">{option}</span>
                  </label>)}
              </div>
            </FormControl>
            <FormDescription className="font-default">בחר את המענה המתאים ביותר לצרכי המטופל בתיווך רפואי מקצועי</FormDescription>
            <FormMessage />
          </FormItem>} />

      
      <FormField control={control} name="additional_notes" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default">מידע נוסף שחשוב שנדע</FormLabel>
            <FormControl>
              <StyledTextarea placeholder="דמנציה, אלצהיימר, שיקום, השגחה בבית חולים" {...field} className="focus:ring-accent" />
            </FormControl>
            <FormDescription className="font-default">לדוגמה: צורך בשפה מסוימת, ציוד רפואי קיים וכו'.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-3 text-destructive">
          <span className="text-lg"></span>
          <p className="font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full text-base h-12 px-6 py-6 bg-accent hover:bg-accent/90 text-white font-default shadow-lg shadow-accent/20 transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin ml-2 h-5 w-5" />
            שולח פנייה...
          </> : 'קבל מטפל רפואי מקצועי תוך 5 שעות'}
      </Button>
    </Form>;
}
export default function OnlineAppointmentBooking() {
  return <section id="סיוע-מיידי-מחכה-לך" className="w-full bg-background text-foreground" dir="rtl">
      <style dangerouslySetInnerHTML={{
      __html: `
        
        html { direction: rtl; }
        
        body { text-align: right; }
        
        .text-left { text-align: right !important; }
      `
    }} />
      <div className="flex flex-col lg:flex-row min-h-screen">
        
        
        <div className="w-full lg:w-1/2 p-6 md:p-12 lg:p-16 xl:p-24 flex flex-col justify-center bg-background">
          <div className="max-w-2xl mx-auto w-full">
            <div className="mb-8 space-y-4">
              <div className="inline-flex items-center justify-center p-2 bg-secondary/10 rounded-lg mb-4">
                <SafeHydrate><CalendarIcon className="w-6 h-6 text-secondary" /></SafeHydrate>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-primary font-default">
                סיוע סיעודי מיידי
              </h2>
              <p className="text-lg text-muted-foreground font-default">
                טיפול רפואי בדמנציה, שיקום לאחר אשפוז, השגחה בבית חולים. מטפל סיעודי ישראלי אצלך בבית תוך פחות מ-5 שעות. השאירו פרטים ונחזור אליכם תוך 15 דקות לתיאום מלא.
              </p>
            </div>

            <FormProvider formKey="immediate_assistance_form_home" formId="immediate_assistance_form_home" sectionName="appointment-section" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            appointment_date: "",
            appointment_time: "",
            visit_reason: "",
            additional_notes: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true,
              pattern: "phone"
            },
            appointment_date: {
              required: true
            },
            appointment_time: {
              required: true
            },
            visit_reason: {
              required: true
            }
          }} submitText="שלחו עכשיו" successMessage="תודה רבה! הפנייה התקבלה במערכת. מטפל סיעודי מוסמך יצור איתך קשר טלפוני תוך 15 דקות לתיאום הגעה." errorMessage="אירעה שגיאה בשליחת הטופס. אנא נסו שנית או התקשרו למוקד החירום שלנו." className="w-full">
              <AppointmentFormContent />
            </FormProvider>
          </div>
        </div>

        
        <div className="w-full lg:w-1/2 relative min-h-[400px] lg:min-h-full bg-muted">
          <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d108504.93649668613!2d34.779862!3d32.085299!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x151d4ca6193b7c1f%3A0xc1fb72a2c0963f90!2sTel%20Aviv-Yafo!5e0!3m2!1sen!2sil!4v1625641234567!5m2!1sen!2sil" width="100%" height="100%" style={{
          border: 0,
          filter: "grayscale(0.1) contrast(1.05)"
        }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="absolute inset-0 w-full h-full object-cover" title="פריסה ארצית מלאה" />
          
          
          <div className="absolute bottom-6 right-6 left-6 md:left-auto md:right-12 md:w-80 bg-card/95 backdrop-blur-md p-6 rounded-xl shadow-2xl border border-secondary/20">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-secondary shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold text-primary font-default">מוקד פריסה ארצית</h4>
                  <p className="text-sm text-muted-foreground font-default">זמינות בבית הלקוח ובבתי החולים</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-secondary shrink-0" />
                <p className="text-sm text-primary font-default" dir="ltr">+972 3-900-5000</p>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-secondary shrink-0" />
                <p className="text-sm text-primary font-default">care@siudy.co.il</p>
              </div>

              <div className="flex items-start gap-3 pt-2 border-t border-border">
                <Clock className="w-5 h-5 text-secondary shrink-0 mt-1" />
                <div>
                  <p className="text-xs font-bold text-primary mb-1 font-default">מוקד חירום 24/7:</p>
                  <p className="text-xs text-muted-foreground font-default">זמינות מלאה בכל שעות היממה</p>
                  <p className="text-xs text-muted-foreground font-default">מענה אנושי מובטח</p>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>;
}